"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";

interface MobileMenuProps {
    isOpen: boolean;
    onClose: () => void;
    links: { href: string; label: string }[];
}

export function MobileMenu({ isOpen, onClose, links }: MobileMenuProps) {
    const pathname = usePathname();
  const { locale, setLocale, t } = useI18n();

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    {/* Menu panel */}
                    <motion.nav
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 z-[70] flex h-full w-72 flex-col bg-dev-bg-dark/95 backdrop-blur-xl border-l border-white/5"
                    >
                        {/* Close button */}
                        <div className="flex justify-end p-6">
                            <button
                                onClick={onClose}
                                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-dev-text-muted transition-colors hover:bg-white/10 hover:text-white"
                                aria-label="Close menu"
                            >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Links */}
                        <ul className="flex flex-col gap-1 px-4">
                            {links.map((link, i) => {
                                const isActive =
                                    link.href === "/"
                                        ? pathname === "/"
                                        : pathname.startsWith(link.href.replace(/\/$/, ""));
                                return (
                                    <motion.li
                                        key={link.href}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.1 + i * 0.05 }}
                                    >
                                        <Link
                                            href={link.href}
                                            onClick={onClose}
                                            className={`block rounded-xl px-4 py-3 text-lg font-medium transition-all duration-200 ${isActive
                                                    ? "bg-dev-blue/10 text-dev-cyan"
                                                    : "text-dev-text-secondary hover:bg-white/5 hover:text-white"
                                                }`}
                                        >
                                            {link.label}
                                        </Link>
                                    </motion.li>
                                );
                            })}
                        </ul>

                        {/* Language switcher + Bottom decoration */}
                        <div className="mt-auto p-6 space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-mono uppercase tracking-wide text-dev-text-muted">
                                    {t("common.language")}
                                </span>
                                <div className="inline-flex items-center rounded-full bg-white/5 p-1">
                                    <button
                                        type="button"
                                        onClick={() => setLocale("vi")}
                                        className={`px-3 py-1 text-xs font-mono rounded-full transition-colors ${locale === "vi"
                                                ? "bg-dev-cyan text-black"
                                                : "text-dev-text-muted hover:text-white"
                                            }`}
                                    >
                                        VI
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setLocale("en")}
                                        className={`px-3 py-1 text-xs font-mono rounded-full transition-colors ${locale === "en"
                                                ? "bg-dev-cyan text-black"
                                                : "text-dev-text-muted hover:text-white"
                                            }`}
                                    >
                                        EN
                                    </button>
                                </div>
                            </div>
                            <div className="h-px bg-gradient-to-r from-transparent via-dev-blue/30 to-transparent" />
                            <p className="text-center font-mono text-xs text-dev-text-muted">
                                TVH.dev
                            </p>
                        </div>
                    </motion.nav>
                </>
            )}
        </AnimatePresence>
    );
}
